import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import _ from "lodash";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import {
  CustomEvent,
  CustomEventSchema,
  EventAttribute,
  EventSchemaPayload,
  ImportedCustomEventSchema,
} from "types/customEvents";
import { ListResponse, ListingProps } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { getEnvVariable } from "utilities/env";

const dateTimeRegex =
  /(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?([+-]\d{2}:?\d{2}|Z)$/;
export const isISOStringWithTimezone = (date: string) =>
  dateTimeRegex.test(date);

export const displayEventAttribute = (value: EventAttribute) => {
  return `${
    typeof value === "string" && isISOStringWithTimezone(value)
      ? OLDate(value)
      : value
  }`;
};

export const parseEvents = (event: { [name: string]: EventAttribute }) =>
  _.mapValues(event, displayEventAttribute);

export const useEvents = (props?: { tenantCode?: string }) => {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();
  const tenant = props?.tenantCode || code;

  const useListOfCustomEvents = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<CustomEvent>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/customEvent${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useCustomEventDetails = (
    customEventID?: string,
  ): SWRResponse<CustomEvent> => {
    return useSWR(
      () =>
        code && customEventID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/customEvent/${customEventID}`
          : null,
      fetcher,
    );
  };

  const addCustomEventSchema = async (values: EventSchemaPayload) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/customEvent/schema`,
      {
        body: JSON.stringify({ customEventSchema: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const editCustomEventSchema = async (
    customEventSchemaID: string,
    values: EventSchemaPayload,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/customEvent/schema/${customEventSchemaID}`,
      {
        body: JSON.stringify({ customEventSchema: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useListOfCustomEventsSchema = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<CustomEventSchema>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/customEvent/schema${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteCustomEventsSchemaList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<CustomEventSchema>>(
      (index) =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/customEvent/schema${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const useCustomEventSchemaDetails = (
    customEventSchemaID?: string,
  ): SWRResponse<CustomEventSchema> => {
    return useSWR(
      () =>
        code && customEventSchemaID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/customEvent/schema/${customEventSchemaID}`
          : null,
      fetcher,
    );
  };

  const activateSchema = async (
    customEventSchema: string,
    isActive: boolean,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/customEvent/schema/${customEventSchema}/active`,
      {
        body: JSON.stringify({ active: isActive }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const addCustomEventSchemaBatch = async (
    values: ImportedCustomEventSchema[],
  ) => {
    if (!code) return;

    const responses = await Promise.all(
      values.map(({ isSaved, ...value }) =>
        isSaved
          ? new Promise((resolve) =>
              resolve({
                isSaved,
                ...value,
              }),
            )
          : fetcher(
              `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/customEvent/schema`,
              {
                body: JSON.stringify({
                  customEventSchema: {
                    ...value,
                    active: { isActive: value.active },
                  },
                }),
                headers: { "Content-Type": "application/json" },
                method: "POST",
              },
            ),
      ),
    )
      .then((results) =>
        Promise.all(
          results.map((r, i) => {
            return r.status === 400
              ? r.json()
              : { data: { ...values[i], isSaved: true } };
          }),
        ),
      )
      .then((resp) =>
        resp.map((r, i) => {
          return {
            ...r,
            ...(r.code === 400 && { data: values[i] }),
          };
        }),
      );

    return responses;
  };

  return {
    addCustomEventSchema,
    editCustomEventSchema,
    useCustomEventDetails,
    useCustomEventSchemaDetails,
    useListOfCustomEvents,
    useListOfCustomEventsSchema,
    activateSchema,
    addCustomEventSchemaBatch,
    useInfiniteCustomEventsSchemaList,
  };
};
